/**
 * What a partner member may do in a customer tenant. Two limits meet:
 *
 *  - the customer's key: the custom role of the API user the customer created
 *    for the partner, read from the tenant's /api/admin-roles/me and folded
 *    into six areas (the Control Center's own sections);
 *  - the member's partner role.
 *
 * Effective access is the lower of the two, per area. Shared by the Worker
 * (enforcement in the Control Center proxy) and the console (scope strips,
 * locked pages).
 */

export type AreaLevel = "rw" | "r" | "none";

export const AREAS = [
  { key: "devices", label: "Devices", icon: "laptop" },
  { key: "access", label: "Access Control", icon: "shield-keyhole" },
  { key: "network", label: "Private Network", icon: "globe-shield" },
  { key: "zera", label: "ZERA", icon: "window-shield" },
  { key: "identity", label: "Identity", icon: "people" },
  { key: "insights", label: "Insights", icon: "data-trending" },
] as const;

export type AreaKey = (typeof AREAS)[number]["key"];
export type Scopes = Record<AreaKey, AreaLevel>;

export const NONE: Scopes = { devices: "none", access: "none", network: "none", zera: "none", identity: "none", insights: "none" };
export const FULL: Scopes = { devices: "rw", access: "rw", network: "rw", zera: "rw", identity: "rw", insights: "rw" };

export type Role = "owner" | "admin" | "technician" | "account_manager" | "billing";

/**
 * `features`: changes a customer tenant's features (the partner's part of its
 * platform policy, which the Brain issues). Owner and Admin only.
 */
export const ROLES: { key: Role; label: string; description: string; scopes: Scopes; business: boolean; team: boolean; tenants: boolean; features: boolean }[] = [
  { key: "owner", label: "Owner", description: "Everything, including billing, price book and partner settings.", scopes: FULL, business: true, team: true, tenants: true, features: true },
  { key: "admin", label: "Admin", description: "All tenants and the team. No price book or payouts.", scopes: FULL, business: true, team: true, tenants: true, features: true },
  {
    key: "technician", label: "Technician", description: "Works in assigned tenants. Identity and Insights are read only.",
    scopes: { devices: "rw", access: "rw", network: "rw", zera: "rw", identity: "r", insights: "r" }, business: false, team: false, tenants: true, features: false,
  },
  {
    key: "account_manager", label: "Account manager", description: "Reads assigned tenants and changes their contract and reports. Sees revenue and renewals. Doesn't work alerts.",
    scopes: { devices: "r", access: "r", network: "r", zera: "r", identity: "r", insights: "r" }, business: true, team: false, tenants: true, features: false,
  },
  { key: "billing", label: "Billing", description: "Billing, invoices and reports. No tenant access.", scopes: NONE, business: true, team: false, tenants: false, features: false },
];

export const ROLE = Object.fromEntries(ROLES.map((r) => [r.key, r])) as Record<Role, (typeof ROLES)[number]>;

const RANK: Record<AreaLevel, number> = { none: 0, r: 1, rw: 2 };

export function lower(a: AreaLevel, b: AreaLevel): AreaLevel {
  return RANK[a] <= RANK[b] ? a : b;
}

export function effective(role: Role, key: Scopes): Scopes {
  const r = ROLE[role]?.scopes ?? NONE;
  return Object.fromEntries(AREAS.map((a) => [a.key, lower(r[a.key], key[a.key])])) as Scopes;
}

/** Folds a tenant's /api/admin-roles/me answer into the six areas. */
export function scopesFromRoles(me: { base_role?: string; features?: Record<string, { level: string }> }): Scopes {
  if (me.base_role === "owner" || me.base_role === "admin") return { ...FULL };
  const out: Scopes = { ...NONE };
  for (const [leaf, f] of Object.entries(me.features ?? {})) {
    const section = leaf.split(".")[0] as AreaKey;
    if (!(section in out)) continue;
    const lvl: AreaLevel = f.level === "write" || f.level === "custom" ? "rw" : f.level === "read" ? "r" : "none";
    if (RANK[lvl] > RANK[out[section]]) out[section] = lvl;
  }
  if (me.base_role === "network_admin") {
    out.network = "rw";
    out.access = "rw";
  }
  if (me.base_role === "auditor") for (const a of AREAS) if (out[a.key] === "none") out[a.key] = "r";
  return out;
}

/**
 * Reads every role holder may make in the tenant (Core's baseline routes):
 * who the caller is, the account, their own roles and preferences, the
 * instance, the list of devices waiting for approval, and the tenant's
 * platform policy (what its plan includes). The Control Center makes them on
 * every page, so they pass whatever areas are shared.
 */
const BASELINE_READS = /^(users\/current|accounts$|admin-roles\/me|me\/preferences|instance|locations\/countries|pending-approvals$|platform\/policy$)/;

export function isBaselineRead(method: string, path: string): boolean {
  return (method === "GET" || method === "HEAD") && BASELINE_READS.test(path.replace(/^\/api\//, "").replace(/\?.*$/, ""));
}

/** The area an API path belongs to, for the proxy's per-area check. null: no area (read anywhere, never written). */
export function areaOfPath(path: string): AreaKey | null {
  const p = path.replace(/^\/api\//, "");
  if (/^(peers|setup-keys|agent-profiles|pending-approvals)/.test(p)) return "devices";
  if (/^(policies|posture-checks|browser-security|access-diagnostics)/.test(p)) return "access";
  if (/^(networks|routes|dns|nameservers|exit-nodes)/.test(p)) return "network";
  if (/^(reverse-proxies|workspace|secure-sessions|zera)/.test(p)) return "zera";
  if (/^(users|groups|admin-roles|identity-providers|directory-sync|integrations\/directory)/.test(p)) return "identity";
  if (/^(events|logs|network-flows|traffic)/.test(p)) return "insights";
  return null;
}

/** Whether a role works alerts (take, release, resolve, snooze, ticket). */
export function worksAlerts(role: Role): boolean {
  return role === "owner" || role === "admin" || role === "technician";
}

/** The scope strip's six squares, compactly: "rw rw r - r -". */
export function scopeSummary(s: Scopes): string {
  const full = AREAS.filter((a) => s[a.key] !== "none").length;
  return full === 6 && AREAS.every((a) => s[a.key] === "rw") ? "Full access" : `${full} of 6 areas`;
}

export const TIERS = [
  { key: "enterprise", label: "Enterprise", critical_min: 15, warning_min: 240 },
  { key: "business", label: "Business", critical_min: 30, warning_min: 480 },
  { key: "essentials", label: "Essentials", critical_min: 60, warning_min: 1440 },
] as const;
